import { QueryClient } from '@tanstack/react-query'

/**
 * Configured QueryClient for IMC Graphs Dashboard
 *
 * Default options:
 * - staleTime: 5 minutes - Data considered fresh, no background refetch
 * - gcTime: 30 minutes - Unused data kept in cache for quick re-display
 * - retry: 1 - Single retry on failure (network issues)
 * - refetchOnWindowFocus: false - Don't refetch when tab regains focus
 *
 * These defaults optimize for dashboard use case where:
 * - Data doesn't change frequently (runs complete, then static)
 * - User expects consistent data during session
 * - Unnecessary refetches waste API calls
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (was cacheTime in v4)
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0, // Don't retry mutations (user should see error and retry manually)
    },
  },
})

/**
 * Query key factory for consistent cache keys
 *
 * Usage:
 * - queryKeys.runs.all -> ['runs']
 * - queryKeys.runs.detail('123') -> ['runs', '123']
 * - queryKeys.runs.list({ status: 'completed' }) -> ['runs', 'list', { status: 'completed' }]
 */
export const queryKeys = {
  runs: {
    all: ['runs'] as const,
    lists: () => [...queryKeys.runs.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.runs.lists(), filters] as const,
    details: () => [...queryKeys.runs.all, 'detail'] as const,
    detail: (fileId: string) => [...queryKeys.runs.details(), fileId] as const,
  },
  organizations: {
    all: ['organizations'] as const,
  },
  phenotypes: {
    all: ['phenotypes'] as const,
    lists: () => [...queryKeys.phenotypes.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.phenotypes.lists(), filters] as const,
    byOrg: (orgId: string) => [...queryKeys.phenotypes.all, 'org', orgId] as const,
    details: () => [...queryKeys.phenotypes.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.phenotypes.details(), id] as const,
  },
  auth: {
    all: ['auth'] as const,
    status: () => [...queryKeys.auth.all, 'status'] as const,
  },
  stats: {
    all: ['stats'] as const,
    dashboard: () => [...queryKeys.stats.all, 'dashboard'] as const,
    health: () => [...queryKeys.stats.all, 'health'] as const,
  },
  logs: {
    all: ['logs'] as const,
    details: () => [...queryKeys.logs.all, 'detail'] as const,
    detail: (fileId: string) => [...queryKeys.logs.details(), fileId] as const,
  },
  examples: {
    all: ['examples'] as const,
    lists: () => [...queryKeys.examples.all, 'list'] as const,
    list: (phenotypeId: string) => [...queryKeys.examples.lists(), phenotypeId] as const,
  },
  codegen: {
    all: ['codegen'] as const,
    byPhenotype: (phenotypeId: string) => [...queryKeys.codegen.all, 'phenotype', phenotypeId] as const,
    detail: (jobId: string) => [...queryKeys.codegen.all, 'job', jobId] as const,
  },
  extractors: {
    all: ['extractors'] as const,
    versions: (phenotypeId: string) => [...queryKeys.extractors.all, 'versions', phenotypeId] as const,
    deployed: (phenotypeId: string) => [...queryKeys.extractors.all, 'deployed', phenotypeId] as const,
    uploads: (phenotypeId: string) => [...queryKeys.extractors.all, 'uploads', phenotypeId] as const,
  },
}
